<template>
  <div class="accessorial-edit">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${id}-accessorial`"
      :label="t('referenceData.accessorials')"
      :errors="v$.naturalId.$errors"
    >
      <div class="accessorial-edit-value-container">
        <RygenDropdown
          :id="inputId"
          :model-value="selectedAccessorialCode"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="accessorialCodes"
          class="flex-grow-1"
          :disabled="disabled"
          option-label="name"
          data-key="naturalId"
          filter
          :placeholder="t('referenceData.accessorials')"
          @update:model-value="internalValue = $event"
        >
          <template #value="{ value, placeholder }">
            {{ value?.name ?? placeholder }}
          </template>
        </RygenDropdown>

        <InlineRemoveButton
          :disabled="disabled"
          class="w-auto"
          @click="emit('remove', internalValue)"
        />
      </div>
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useModelValue,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { AccessorialCode } from '@rygen/rygen-ui/model';

import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('rate-result-accessorial-edit').uid,
  },
  modelValue: {
    type: AccessorialCode,
    default: () => new AccessorialCode(),
  },
  accessorialCodes: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, accessorialCodes, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', code: AccessorialCode): void;
  (event: 'update:model-value', newVal: AccessorialCode): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => new AccessorialCode(opts),
});

const selectedAccessorialCode = computed(() =>
  internalValue.value.naturalId ? internalValue.value : undefined,
);

const rules = {
  naturalId: {
    required: required(t('referenceData.accessorials')),
  },
};
const { v$ } = useValidations(internalValue, rules);
</script>

<style>
.accessorial-edit {
  display: flex;
  gap: var(--gap-1);

  .input-wrapper {
    flex: 1 0 auto;

    .accessorial-edit-value-container {
      display: flex;
      gap: var(--gap-1);
      align-items: center;

      & > :first-child {
        /* the input should grow to fill the container */
        flex: 1 0 auto;
      }
    }
  }
}
</style>
